import React, { useEffect, useState, useMemo, forwardRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { TableVirtuoso } from 'react-virtuoso'

import {
  Button,
  Container,
  IconButton,
  TextField,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
  Box,
  Divider,
  Paper,
  TableContainer,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
} from '@mui/material'

import {
  DeleteOutlined as DeleteOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  ArrowBack as ArrowBackIcon,
  InfoOutlined,
  HighlightOff as HighlightOffIcon,
} from '@mui/icons-material/'

import { TOAST_SETTINGS_ERROR } from '../../../utility/constants'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { getAxiosOptions } from '../../../services/serviceHelper'

const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

// Named display components to avoid react/display-name warnings
const VirtuosoScroller = forwardRef((props, ref) => (
  <TableContainer component={Paper} elevation={0} {...props} ref={ref} sx={{ height: '100%', overflowY: 'auto' }} />
))
VirtuosoScroller.displayName = 'VirtuosoScroller'

const VirtuosoTable = (props) => <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
VirtuosoTable.displayName = 'VirtuosoTable'

const VirtuosoTableHead = forwardRef((props, ref) => <TableHead {...props} ref={ref} sx={{ backgroundColor: '#f9fafb', zIndex: 10 }} />)
VirtuosoTableHead.displayName = 'VirtuosoTableHead'

const VirtuosoTableRow = (props) => <TableRow {...props} hover />
VirtuosoTableRow.displayName = 'VirtuosoTableRow'

const VirtuosoTableBody = forwardRef((props, ref) => <TableBody {...props} ref={ref} />)
VirtuosoTableBody.displayName = 'VirtuosoTableBody'

const VirtuosoTableComponents = {
  Scroller: VirtuosoScroller,
  Table: VirtuosoTable,
  TableHead: VirtuosoTableHead,
  TableRow: VirtuosoTableRow,
  TableBody: VirtuosoTableBody,
}

// Simple debounce hook for search input
function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return debounced
}

// Expandable cell to display metadata keys and values
/* eslint-disable react/prop-types */
const MetadataCell = ({ item, keys }) => {
  const [expanded, setExpanded] = useState(false)

  if (!keys || keys.length === 0) {
    return (
      <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', fontSize: '11px' }}>
        None
      </Typography>
    )
  }

  const displayedKeys = expanded ? keys : keys.slice(0, 2)
  const hasMore = keys.length > 2

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      {displayedKeys.map((key) => {
        const val = item.metadata?.[key] || ''
        return (
          <Box key={key} display="flex" gap={0.5} alignItems="flex-start">
            <Typography variant="caption" fontWeight="600" color="textSecondary" sx={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
              {key}:
            </Typography>
            <Typography variant="caption" color="textPrimary" sx={{ fontSize: '11px', wordBreak: 'break-all' }}>
              {val || <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>empty</span>}
            </Typography>
          </Box>
        )
      })}
      {hasMore && (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            setExpanded(!expanded)
          }}
          sx={{
            p: 0,
            minWidth: 0,
            fontSize: '10px',
            textTransform: 'none',
            fontWeight: 600,
            color: 'primary.main',
            alignSelf: 'flex-start',
            mt: 0.25,
            height: 'auto',
            lineHeight: 1.2,
            '&:hover': { background: 'none', textDecoration: 'underline' },
          }}
        >
          {expanded ? 'Show Less' : `Show ${keys.length - 2} More`}
        </Button>
      )}
    </Box>
  )
}
/* eslint-enable react/prop-types */

const ListItemsConfig = () => {
  const { listId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthContext()

  const [listInfo, setListInfo] = useState(null)
  const [listData, setListData] = useState([])
  const [metadataKeys, setMetadataKeys] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [unsavedItems, setUnsavedItems] = useState([])

  // Search filter
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 200)

  // Pagination states
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)

  // Dialog form states
  const [isItemDialogOpen, setIsItemDialogOpen] = useState(false)
  const [formMode, setFormMode] = useState('add') // 'add' or 'edit'
  const [activeItemId, setActiveItemId] = useState(null)
  const [formValue, setFormValue] = useState('')
  const [formMetadata, setFormMetadata] = useState({})
  const [isSaving, setIsSaving] = useState(false)

  // Deletion confirmation
  const [deletingItemId, setDeletingItemId] = useState(-1)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)
  const [checkingUsage, setCheckingUsage] = useState(false)
  const [usageData, setUsageData] = useState({ inUse: false, uses: [] })

  const handleOpenDeleteConfirmation = (item) => {
    setDeletingItemId(item.id)
    setIsDeleteConfirmationOpen(true)
    setCheckingUsage(true)
    setUsageData({ inUse: false, uses: [] })
    axios
      .get(`${NODE_BASE_URL}/admin/template/list/check-list-data-usage/${item.id}`, getAxiosOptions(user))
      .then((res) => {
        setUsageData(res.data || { inUse: false, uses: [] })
      })
      .catch((err) => {
        console.error('Failed to check list item usage:', err)
      })
      .finally(() => {
        setCheckingUsage(false)
      })
  }

  // Combined data fetching hook to avoid setState in effect warnings and satisfy hooks deps rules
  useEffect(() => {
    if (!user || !listId) return

    const loadData = async () => {
      setIsLoading(true)
      try {
        const response = await axios.get(`${NODE_BASE_URL}/template/list/company`, getAxiosOptions(user))
        const currList = response.data.find((l) => String(l.id) === String(listId))
        if (currList) {
          setListInfo(currList)
          const keys = currList.metadata_keys ? currList.metadata_keys.split(',').map((k) => k.trim()) : []
          setMetadataKeys(keys)
        } else {
          toast.error('List not found.', TOAST_SETTINGS_ERROR)
          navigate('/admin/lists')
        }
      } catch (err) {
        console.error('Failed to fetch list configuration:', err)
        toast.error('Failed to fetch list configuration.', TOAST_SETTINGS_ERROR)
      }

      try {
        const response = await axios.get(`${NODE_BASE_URL}/template/list-data/list/${listId}`, getAxiosOptions(user))
        setListData(response.data || [])
      } catch (err) {
        console.error('Failed to load list items:', err)
        toast.error('Failed to load list items.', TOAST_SETTINGS_ERROR)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [user, listId, navigate])

  const refreshListData = async () => {
    if (!user) return
    try {
      const response = await axios.get(`${NODE_BASE_URL}/template/list-data/list/${listId}`, getAxiosOptions(user))
      setListData(response.data || [])
    } catch (err) {
      console.error('Failed to reload list items:', err)
      toast.error('Failed to load list items.', TOAST_SETTINGS_ERROR)
    }
  }

  // Optimize duplicate checking: constant-time set lookups
  const duplicatesSet = useMemo(() => {
    const counts = {}
    for (const item of listData) {
      if (item?.value) {
        const val = item.value.trim()
        counts[val] = (counts[val] || 0) + 1
      }
    }
    for (const item of unsavedItems) {
      if (item?.value) {
        const val = item.value.trim()
        counts[val] = (counts[val] || 0) + 1
      }
    }
    const duplicates = new Set()
    for (const val in counts) {
      if (counts[val] > 1) {
        duplicates.add(val)
      }
    }
    return duplicates
  }, [listData, unsavedItems])

  // Filter list data based on search input
  const filteredData = useMemo(() => {
    if (!debouncedSearchQuery) return listData
    const q = debouncedSearchQuery.toLowerCase()
    return listData.filter((item) => item.value && item.value.toLowerCase().includes(q))
  }, [listData, debouncedSearchQuery])

  // Combine unsaved items with filtered list data
  const allTableData = useMemo(() => {
    return [...unsavedItems, ...filteredData]
  }, [unsavedItems, filteredData])

  // Sliced data for pagination
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage
    return allTableData.slice(start, start + rowsPerPage)
  }, [allTableData, page, rowsPerPage])

  // Dialog handlers
  const handleOpenAddDrawer = () => {
    setFormMode('add')
    setActiveItemId(null)
    setFormValue('')
    const defaultMeta = {}
    metadataKeys.forEach((key) => {
      defaultMeta[key] = ''
    })
    setFormMetadata(defaultMeta)
    setIsItemDialogOpen(true)
  }

  const handleOpenEditDrawer = (item) => {
    setFormMode('edit')
    setActiveItemId(item.id)
    setFormValue(item.value || '')
    const itemMeta = {}
    metadataKeys.forEach((key) => {
      itemMeta[key] = item.metadata?.[key] || ''
    })
    setFormMetadata(itemMeta)
    setIsItemDialogOpen(true)
  }

  const handleSaveItem = async () => {
    if (!user || !formValue.trim()) return

    setIsSaving(true)
    try {
      if (formMode === 'add') {
        const enteredLines = formValue
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)

        // Filter out items already existing in the list to avoid duplicates
        const existingValues = new Set(listData.map((it) => it.value))
        const uniqueNewLines = [...new Set(enteredLines)].filter((line) => !existingValues.has(line))

        if (uniqueNewLines.length === 0) {
          toast.info('No new unique items to add.')
          setIsItemDialogOpen(false)
          return
        }

        // Send array of values if there are multiple, or single string if there's only one
        const payloadValue = uniqueNewLines.length === 1 ? uniqueNewLines[0] : uniqueNewLines

        await axios.post(
          `${NODE_BASE_URL}/admin/template/list/add-list-data`,
          {
            list_id: listId,
            value: payloadValue,
            metadata: formMetadata,
          },
          getAxiosOptions(user)
        )
        toast.success(uniqueNewLines.length === 1 ? 'List item added successfully.' : 'List items added successfully in bulk.')
      } else {
        const originalItem = listData.find((item) => item.id === activeItemId)

        // 1. Edit list item name/value if changed
        if (formValue.trim() !== originalItem?.value) {
          await axios.put(
            `${NODE_BASE_URL}/admin/template/list/edit-list-data-name`,
            {
              list_data_id: activeItemId,
              list_data_name: formValue.trim(),
            },
            getAxiosOptions(user)
          )
        }

        // 2. Edit metadata values if changed
        for (const mdk of metadataKeys) {
          const originalVal = originalItem?.metadata?.[mdk] || ''
          const newVal = formMetadata[mdk] || ''
          if (originalVal !== newVal) {
            await axios.put(
              `${NODE_BASE_URL}/admin/template/list/edit-list-data-metadata`,
              {
                list_data_id: activeItemId,
                metadata_key: mdk,
                metadata_value: newVal,
              },
              getAxiosOptions(user)
            )
          }
        }
        toast.success('List item saved successfully.')
      }

      setIsItemDialogOpen(false)
      await refreshListData()
    } catch (err) {
      console.error('Failed to save list item details:', err)
      toast.error('Failed to save list item details.', TOAST_SETTINGS_ERROR)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteItem = async (itemId) => {
    if (!user) return
    try {
      await axios.delete(
        `${NODE_BASE_URL}/admin/template/list/delete-list-data/${itemId}`,
        getAxiosOptions(user)
      )
      toast.success('List item deleted successfully.')
      await refreshListData()
    } catch (err) {
      console.error('Failed to delete list item:', err)
      const errMsg = err.response?.data?.error || 'Failed to delete list item.'
      toast.error(errMsg, TOAST_SETTINGS_ERROR)
    }
  }

  const handleAddRow = () => {
    const tempId = `temp_${Date.now()}_${Math.random()}`
    const defaultMeta = {}
    metadataKeys.forEach((key) => {
      defaultMeta[key] = ''
    })
    setUnsavedItems((prev) => [
      {
        id: tempId,
        value: '',
        metadata: defaultMeta,
        isNew: true,
      },
      ...prev,
    ])
    setPage(0)
  }

  const handleRemoveUnsavedItem = (tempId) => {
    setUnsavedItems((prev) => prev.filter((item) => item.id !== tempId))
  }

  const handleUpdateUnsavedValue = (tempId, val) => {
    setUnsavedItems((prev) =>
      prev.map((item) => (item.id === tempId ? { ...item, value: val } : item))
    )
  }

  const handleUpdateUnsavedMetadata = (tempId, key, val) => {
    setUnsavedItems((prev) =>
      prev.map((item) =>
        item.id === tempId
          ? { ...item, metadata: { ...item.metadata, [key]: val } }
          : item
      )
    )
  }

  const handleSaveBulkRows = async () => {
    if (!user || unsavedItems.length === 0) return

    // Filter out rows with empty values
    const validRows = unsavedItems.filter((row) => row.value.trim() !== '')
    if (validRows.length === 0) {
      toast.warning('No unsaved rows have values to save.')
      return
    }

    // Check for duplicates inside validRows before submitting
    const seenValues = new Set()
    for (const row of validRows) {
      const val = row.value.trim()
      if (seenValues.has(val)) {
        toast.error(`Duplicate value detected in new rows: "${val}". Please ensure unique values.`, TOAST_SETTINGS_ERROR)
        return
      }
      seenValues.add(val)
      if (listData.some((ld) => ld.value === val)) {
        toast.error(`Value "${val}" already exists in the list. Please ensure unique values.`, TOAST_SETTINGS_ERROR)
        return
      }
    }

    setIsSaving(true)
    try {
      const payloadValue = validRows.map((row) => ({
        value: row.value.trim(),
        metadata: row.metadata,
      }))

      await axios.post(
        `${NODE_BASE_URL}/admin/template/list/add-list-data`,
        {
          list_id: listId,
          value: payloadValue,
        },
        getAxiosOptions(user)
      )
      toast.success(`${validRows.length} item(s) saved successfully.`)
      setUnsavedItems([])
      await refreshListData()
    } catch (err) {
      console.error('Failed to save bulk rows:', err)
      toast.error('Failed to save new items.', TOAST_SETTINGS_ERROR)
    } finally {
      setIsSaving(false)
    }
  }

  // Check duplicates local validator
  const isDuplicateLocal = useMemo(() => {
    const checkValue = formValue.trim()
    if (!checkValue) return false

    if (formMode === 'add') {
      const lines = formValue.split('\n').map((l) => l.trim()).filter(Boolean)
      if (lines.length === 1) {
        return listData.some((it) => it.value === lines[0])
      }
      return false
    }

    return listData.some((it) => it.id !== activeItemId && it.value === checkValue)
  }, [formValue, listData, activeItemId, formMode])

  // Setup fixed percentage column widths
  const colWidthIndex = '7%'
  const colWidthValue = '48%'
  const colWidthMetadata = '33%'
  const colWidthActions = '12%'

  // Fixed Header Renderer for virtuoso
  const renderFixedHeader = () => (
    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
      <TableCell style={{ width: colWidthIndex, fontWeight: 600 }}>#</TableCell>
      <TableCell style={{ width: colWidthValue, fontWeight: 600 }}>Value</TableCell>
      <TableCell style={{ width: colWidthMetadata, fontWeight: 600 }}>Metadata</TableCell>
      <TableCell align="right" style={{ width: colWidthActions, fontWeight: 600, paddingRight: '24px' }}>Actions</TableCell>
    </TableRow>
  )

  // Item Content Row Renderer for virtuoso
  const renderRowContent = (index, item) => {
    const isDuplicate = duplicatesSet.has(item.value?.trim())
    const rowNum = page * rowsPerPage + index + 1

    if (item.isNew) {
      return (
        <>
          <TableCell style={{ width: colWidthIndex, fontSize: '13px', verticalAlign: 'middle', backgroundColor: '#f0fdf4' }}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <span style={{
                backgroundColor: '#22c55e',
                color: 'white',
                fontSize: '10px',
                fontWeight: 'bold',
                padding: '2px 6px',
                borderRadius: '4px',
                textTransform: 'uppercase'
              }}>
                New
              </span>
            </Box>
          </TableCell>
          <TableCell style={{ width: colWidthValue, fontSize: '13px', verticalAlign: 'middle', backgroundColor: '#f0fdf4' }}>
            <Box display="flex" alignItems="center" gap={0.5} width="100%">
              <TextField
                value={item.value}
                onChange={(e) => handleUpdateUnsavedValue(item.id, e.target.value)}
                placeholder="Enter value..."
                size="small"
                variant="outlined"
                fullWidth
                sx={{
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-input': {
                    padding: '6px 10px',
                    fontSize: '13px',
                  }
                }}
                error={isDuplicate}
              />
              {isDuplicate && (
                <Tooltip title="Duplicate list item!">
                  <InfoOutlined style={{ fontSize: 18, color: '#ef4444', marginLeft: '4px', flexShrink: 0 }} />
                </Tooltip>
              )}
            </Box>
          </TableCell>
          <TableCell style={{ width: colWidthMetadata, verticalAlign: 'middle', backgroundColor: '#f0fdf4' }}>
            <Box display="flex" flexDirection="column" gap={1}>
              {metadataKeys.map((key) => (
                <Box key={key} display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" fontWeight="600" color="textSecondary" sx={{ fontSize: '11px', minWidth: '60px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {key}:
                  </Typography>
                  <TextField
                    value={item.metadata?.[key] || ''}
                    onChange={(e) => handleUpdateUnsavedMetadata(item.id, key, e.target.value)}
                    placeholder={`Enter ${key}...`}
                    size="small"
                    variant="outlined"
                    fullWidth
                    sx={{
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-input': {
                        padding: '4px 8px',
                        fontSize: '11px',
                      }
                    }}
                  />
                </Box>
              ))}
              {metadataKeys.length === 0 && (
                <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', fontSize: '11px' }}>
                  None
                </Typography>
              )}
            </Box>
          </TableCell>
          <TableCell align="right" style={{ width: colWidthActions, paddingRight: '24px', verticalAlign: 'middle', backgroundColor: '#f0fdf4' }}>
            <Box display="flex" justifyContent="flex-end">
              <IconButton onClick={() => handleRemoveUnsavedItem(item.id)} size="small" color="error" title="Remove Row">
                <HighlightOffIcon fontSize="small" />
              </IconButton>
            </Box>
          </TableCell>
        </>
      )
    }

    return (
      <>
        <TableCell style={{ width: colWidthIndex, fontSize: '13px', verticalAlign: 'top', color: '#6b7280' }}>
          {rowNum}
        </TableCell>
        <TableCell style={{ width: colWidthValue, whiteSpace: 'pre-line', fontSize: '13px', verticalAlign: 'top' }}>
          <Box display="flex" alignItems="flex-start" gap={0.5}>
            <Box sx={{ flexGrow: 1, wordBreak: 'break-word' }}>
              {item.value}
            </Box>
            {isDuplicate && (
              <Tooltip title="Duplicate list item!">
                <InfoOutlined style={{ fontSize: 18, color: '#ef4444', marginLeft: '4px', flexShrink: 0 }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell style={{ width: colWidthMetadata, verticalAlign: 'top' }}>
          <MetadataCell item={item} keys={metadataKeys} />
        </TableCell>
        <TableCell align="right" style={{ width: colWidthActions, paddingRight: '24px', verticalAlign: 'top' }}>
          <Box display="flex" justifyContent="flex-end" gap={0.5}>
            <IconButton onClick={() => handleOpenEditDrawer(item)} size="small" color="primary" title="Edit Item">
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            {listData.length > 1 && (
              <IconButton
                onClick={() => handleOpenDeleteConfirmation(item)}
                size="small"
                color="error"
                title="Delete Item"
              >
                <DeleteOutlinedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </TableCell>
      </>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 1, height: '90vh', display: 'flex', flexDirection: 'column' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexShrink={0}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton onClick={() => navigate('/admin/lists')} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h6" color="textPrimary" fontWeight="700" >
              {listInfo?.list_name || 'List Items'}
            </Typography>
            {/* <Typography variant="body2" color="textSecondary">
              Manage values and metadata keys for this list definition.
            </Typography> */}
          </Box>
        </Box>
        <Box display="flex" gap={1.5} alignItems="center">
          <TextField
            placeholder="Search items..."
            size="small"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setPage(0)
            }}
            sx={{ width: 220, backgroundColor: 'white' }}
          />
          <Button
            color="primary"
            variant="outlined"
            onClick={handleAddRow}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
          >
            Add Row
          </Button>
          {unsavedItems.length > 0 && (
            <Button
              color="success"
              variant="contained"
              disabled={isSaving}
              onClick={handleSaveBulkRows}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#10b981',
                '&:hover': { backgroundColor: '#059669' },
              }}
            >
              {isSaving ? 'Saving...' : `Save Bulk (${unsavedItems.length})`}
            </Button>
          )}
          <Button
            color="primary"
            variant="contained"
            onClick={handleOpenAddDrawer}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ flexGrow: 1, minHeight: 0, borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" flexDirection="column" gap={1} py={8}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Loading list items...
            </Typography>
          </Box>
        ) : allTableData.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" flexDirection="column" gap={1} py={8}>
            <Typography color="textSecondary" variant="body1">
              No items found.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              <TableVirtuoso
                data={paginatedData}
                components={VirtuosoTableComponents}
                fixedHeaderContent={renderFixedHeader}
                itemContent={renderRowContent}
              />
            </Box>
            <Divider />
            <TablePagination
              component="div"
              count={allTableData.length}
              page={page}
              onPageChange={(event, newPage) => { event?.toString(); setPage(newPage) }}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          </>
        )}
      </Paper>

      {/* Delete Item Confirmation Dialog */}
      <Dialog
        open={isDeleteConfirmationOpen}
        onClose={() => !checkingUsage && setIsDeleteConfirmationOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', boxShadow: '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1, pt: 3 }}>
          <Typography variant="h6" fontWeight="700" color="error.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Delete List Item
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {checkingUsage ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4} gap={2}>
              <CircularProgress size={36} color="error" />
              <Typography variant="body2" color="textSecondary">
                Checking usage in active processes...
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body1" sx={{ color: '#1f2937', lineHeight: 1.5 }}>
                Are you sure you want to delete the item{' '}
                <strong style={{ color: '#111827', backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                  {listData.find((ld) => ld.id === deletingItemId)?.value}
                </strong>
                ?
              </Typography>

              {usageData.inUse && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.2}>
                    <InfoOutlined sx={{ color: '#d97706', fontSize: 18 }} />
                    <Typography variant="subtitle2" color="#b45309" fontWeight="700">
                      Warning: Item is in Use
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="#b45309" sx={{ lineHeight: 1.45 }}>
                    This item is currently selected in{' '}
                    <strong>
                      {(() => {
                        const uniqueNames = [...new Set(usageData.uses.map((u) => u.process_template_name))]
                        if (uniqueNames.length > 1) {
                          return `"${uniqueNames[0]}" and potentially other processes`
                        }
                        return uniqueNames[0] ? `"${uniqueNames[0]}"` : 'active processes'
                      })()}
                    </strong>
                    . Deleting it will hide it from future entry options, but historical records will retain their selections.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setDeletingItemId(-1)
              setIsDeleteConfirmationOpen(false)
            }}
            color="secondary"
            disabled={checkingUsage}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleDeleteItem(deletingItemId)
              setDeletingItemId(-1)
              setIsDeleteConfirmationOpen(false)
            }}
            variant="contained"
            color="error"
            disabled={checkingUsage}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: '8px',
              backgroundColor: '#dc2626',
              '&:hover': { backgroundColor: '#b91c1c' },
            }}
          >
            Delete Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog Modal */}
      <Dialog
        open={isItemDialogOpen}
        onClose={() => !isSaving && setIsItemDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="600">
            {formMode === 'add' ? 'Add New Item' : 'Edit List Item'}
          </Typography>
          <IconButton onClick={() => setIsItemDialogOpen(false)} disabled={isSaving} size="small">
            <HighlightOffIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 3 }}>
          <TextField
            value={formValue}
            label={formMode === 'add' ? 'Item Value(s)' : 'Item Value'}
            placeholder={formMode === 'add' ? 'Enter one item per line to add in bulk...' : ''}
            multiline
            rows={4}
            variant="outlined"
            error={isDuplicateLocal}
            helperText={
              isDuplicateLocal
                ? 'Duplicate list item!'
                : formMode === 'add'
                ? 'Enter one item per line to add multiple items in bulk. Existing/duplicate entries will be skipped automatically.'
                : ''
            }
            onChange={(e) => setFormValue(e.target.value)}
            disabled={isSaving}
            fullWidth
            required
            autoFocus={formMode === 'add'}
          />

          {metadataKeys.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="textSecondary" fontWeight="600" mb={1.5}>
                Metadata Values
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                {metadataKeys.map((key) => (
                  <TextField
                    key={key}
                    label={key}
                    value={formMetadata[key] || ''}
                    variant="outlined"
                    size="small"
                    disabled={isSaving}
                    onChange={(e) => {
                      const val = e.target.value
                      setFormMetadata((prev) => ({ ...prev, [key]: val }))
                    }}
                    fullWidth
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setIsItemDialogOpen(false)} color="secondary" disabled={isSaving} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveItem}
            variant="contained"
            color="primary"
            disabled={isSaving || !formValue.trim() || isDuplicateLocal}
            sx={{ textTransform: 'none', fontWeight: 600, px: 4 }}
          >
            {isSaving ? 'Saving...' : formMode === 'add' ? 'Add Item' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default ListItemsConfig
